/**
 * Translate raw metrics into farmer-friendly status objects.
 * Each returns: { level, label, message, icon, tip (concrete action) }
 */

export const ndviStatus = (v) => {
  if (v == null) return { level: "muted", label: "Keine Daten", message: "Wolken oder Datenlücke", tip: "Warte auf den nächsten wolkenfreien Überflug.", icon: "CloudArrowDown" };
  if (v >= 0.7) return { level: "good", label: "Vegetation sehr gesund", message: "Kräftiges Wachstum, üppige Blattmasse.", tip: "Weitermachen. Keine Eingriffe nötig.", icon: "Plant" };
  if (v >= 0.5) return { level: "ok", label: "Gesunde Vegetation", message: "Bestand ist in gutem Zustand.", tip: "Routine-Kontrolle. Kein Handlungsbedarf.", icon: "Leaf" };
  if (v >= 0.3) return { level: "warn", label: "Schwacher Bestand", message: "Wachstum unter dem Durchschnitt.", tip: "Prüfe auf Nährstoffmangel oder Schädlinge. Düngung überlegen.", icon: "Leaf" };
  return { level: "critical", label: "Stress / kahle Fläche", message: "Wenig grünes Blattwerk sichtbar.", tip: "Vor-Ort-Begehung. Boden beproben und Ursache klären.", icon: "Warning" };
};

export const ndviAnomaly = (v) => {
  if (v == null) return { level: "muted", label: "—", message: "", tip: "", icon: "DotsThree" };
  if (v >= 0.05) return { level: "good", label: "Deutlich besser als üblich", message: "Positiver Trend im Vergleich zum 30-Tage-Mittel.", tip: "Bestand entwickelt sich überdurchschnittlich. Einfach weiterlaufen lassen.", icon: "TrendUp" };
  if (v >= -0.02) return { level: "ok", label: "Wie üblich", message: "Entwicklung im normalen Rahmen.", tip: "Nichts Auffälliges. Normale Pflege fortsetzen.", icon: "ArrowsHorizontal" };
  if (v >= -0.05) return { level: "warn", label: "Leichte Abweichung", message: "Vegetation etwas schwächer als normal.", tip: "Wetter & Bodenfeuchte prüfen. Mögliche Ursachen im Blick behalten.", icon: "TrendDown" };
  return { level: "critical", label: "Auffällig schwächer", message: "Deutlicher Rückgang – bitte genauer prüfen.", tip: "Feldbegehung diese Woche. Auf Trockenstress, Pilze oder Insektenbefall achten.", icon: "TrendDown" };
};

export const soilMoistureStatus = (v) => {
  if (v == null) return { level: "muted", label: "—", message: "", tip: "", icon: "DotsThree" };
  if (v >= 0.35) return { level: "good", label: "Sehr feucht", message: "Böden mit reichlich Wasserreserve.", tip: "Keine Bewässerung. Auf Staunässe bei Starkregen achten.", icon: "Drop" };
  if (v >= 0.25) return { level: "ok", label: "Normale Feuchte", message: "Ausreichend Wasser im Oberboden.", tip: "Alles im grünen Bereich. Keine Bewässerung nötig.", icon: "Drop" };
  if (v >= 0.15) return { level: "warn", label: "Trocken", message: "Bewässerung beobachten.", tip: "Nächste Bewässerung einplanen, falls kein Regen in den nächsten 3–5 Tagen.", icon: "DropHalfBottom" };
  return { level: "critical", label: "Sehr trocken", message: "Akute Trockengefahr für Wurzelzone.", tip: "Jetzt bewässern – vor allem junge Kulturen und flach wurzelnde Pflanzen.", icon: "Sun" };
};

export const soilDeficitStatus = (v) => {
  if (v == null) return { level: "muted", label: "—", message: "", tip: "", icon: "DotsThree" };
  if (v <= 0) return { level: "ok", label: "Kein Defizit", message: "Böden haben Normal- oder Überschuss-Feuchte.", tip: "Wasserhaushalt in Ordnung. Keine Maßnahme.", icon: "Drop" };
  if (v <= 0.03) return { level: "warn", label: "Leichtes Defizit", message: "Erste Anzeichen von Trockenheit.", tip: "Wetterprognose im Auge behalten. Bei anhaltender Trockenheit Bewässerung planen.", icon: "DropHalfBottom" };
  return { level: "critical", label: "Starkes Defizit", message: "Erheblicher Wasserstress möglich.", tip: "Bewässerungs-Priorität: flachwurzelnde Kulturen zuerst. Mulchen hält Restfeuchte.", icon: "Sun" };
};

export const yieldStatus = (v) => {
  if (v == null) return { level: "muted", label: "—", message: "", tip: "", icon: "DotsThree" };
  if (v >= 70) return { level: "good", label: "Gute Erntechancen", message: "Vegetation & Wasser sprechen für eine solide Ernte.", tip: "Erntetermine planen. Lagerlogistik frühzeitig sichern.", icon: "Plant" };
  if (v >= 50) return { level: "ok", label: "Durchschnittliche Ernte", message: "Keine größeren Abweichungen erkennbar.", tip: "Standardplanung. Kein frühzeitiger Verkauf nötig.", icon: "Plant" };
  if (v >= 30) return { level: "warn", label: "Unterdurchschnittlich", message: "Ertrag könnte niedriger als üblich ausfallen.", tip: "Verkauf eventuell staffeln. Kostenseite prüfen – wo kann gespart werden?", icon: "Warning" };
  return { level: "critical", label: "Risiko", message: "Kombination aus NDVI-, Feuchte- und ET₀-Stress.", tip: "Ertragsausfall-Versicherung prüfen. Mit Abnehmern über Lieferbedingungen sprechen.", icon: "WarningCircle" };
};

export const priceStatus = (v) => {
  if (v == null) return { level: "muted", label: "—", message: "", tip: "", icon: "DotsThree" };
  if (v >= 5) return { level: "critical", label: "Preise stark steigend", message: "Futures legen spürbar zu (7 Tage).", tip: "Gutes Fenster zum Verkaufen. Erntekontrakte jetzt prüfen.", icon: "TrendUp" };
  if (v >= 1) return { level: "warn", label: "Preise steigend", message: "Leichter Aufwärtstrend.", tip: "Teilverkäufe erwägen. Markt beobachten.", icon: "TrendUp" };
  if (v >= -1) return { level: "ok", label: "Preise stabil", message: "Kaum Bewegung über die Woche.", tip: "Kein Handlungsdruck. Bei Vorkontrakten bleiben.", icon: "ArrowsHorizontal" };
  if (v >= -5) return { level: "good", label: "Preise fallend", message: "Leichter Abwärtstrend.", tip: "Verkauf eher zurückhalten, falls Lagerkapazität vorhanden.", icon: "TrendDown" };
  return { level: "good", label: "Preise stark fallend", message: "Deutlicher Rückgang der Futures.", tip: "Nicht verkaufen, wenn möglich. Auf Erholung warten oder absichern.", icon: "TrendDown" };
};

export const forestStatus = (v) => {
  if (v == null) return { level: "muted", label: "—", message: "", tip: "", icon: "DotsThree" };
  if (v >= 80) return { level: "good", label: "Wald vital", message: "Gesunde Kronen und Wasserbalance.", tip: "Routine-Kontrolle. Keine Maßnahmen erforderlich.", icon: "Tree" };
  if (v >= 60) return { level: "ok", label: "Wald OK", message: "Keine Auffälligkeiten.", tip: "Saisonale Pflege wie gewohnt durchführen.", icon: "Tree" };
  if (v >= 40) return { level: "warn", label: "Wald angespannt", message: "Leichter Stress in Krone oder Seen.", tip: "Nach Borkenkäfer-Zeichen und welkenden Kronen suchen. Befallstellen markieren.", icon: "TreeEvergreen" };
  return { level: "critical", label: "Wald gefährdet", message: "Kombination aus Trocken- und Wasserqualitäts-Signalen.", tip: "Dringende Begehung. Schadholz zeitnah aufarbeiten, Jungwuchs sichern.", icon: "WarningCircle" };
};

/**
 * Overall region health 0..100 combining sub-indicators.
 */
export const regionHealth = (m) => {
  if (!m) return 50;
  const yf = m.yield_forecast_index ?? 50;
  const fv = m.forest_vitality_index ?? 60;
  const anomPenalty = Math.max(0, -(m.ndvi_anomaly ?? 0)) * 300; // 0..30
  const defPenalty = Math.max(0, (m.soil_moisture_deficit ?? 0)) * 300; // 0..30
  const score = yf * 0.55 + fv * 0.35 - anomPenalty - defPenalty;
  return Math.max(0, Math.min(100, Math.round(score)));
};

export const healthBand = (score) => {
  if (score >= 75) return { level: "good", label: "Sehr gut" };
  if (score >= 55) return { level: "ok", label: "Stabil" };
  if (score >= 35) return { level: "warn", label: "Unter Druck" };
  return { level: "critical", label: "Kritisch" };
};

export const toneColor = (level) => {
  switch (level) {
    case "good": return { bg: "rgba(0,250,154,0.10)", fg: "#00FA9A", dot: "#00FA9A" };
    case "ok": return { bg: "#131513", fg: "#7EE8B0", dot: "#7EE8B0" };
    case "warn": return { bg: "#17130C", fg: "#FFC24B", dot: "#FFC24B" };
    case "critical": return { bg: "rgba(255,85,0,0.10)", fg: "#FF7A45", dot: "#FF7A45" };
    default: return { bg: "#141414", fg: "#A1A1AA", dot: "#8A8A93" };
  }
};

/**
 * Plain-language glossary for scientific metrics.
 * Used as tooltips/expandable explanations on the dashboard.
 */
export const GLOSSARY = {
  NDVI: {
    short: "Vegetations-Gesundheitsindex (0–1)",
    long: "NDVI misst, wie viel grünes Blattwerk deine Fläche hat. Satelliten vergleichen das Sonnenlicht, das die Pflanzen reflektieren. Hoch = gesund & dicht. Niedrig = kahl, gestresst oder schon geerntet.",
    scale: "0.7+ top · 0.5–0.7 gut · 0.3–0.5 schwach · <0.3 kahl",
  },
  NDMI: {
    short: "Wassergehalt in den Blättern (0–1)",
    long: "NDMI zeigt, wie feucht das Blattgewebe selbst ist. Gute Werte bedeuten: Pflanzen haben genug Wasser. Sinkt NDMI, beginnt Trockenstress – oft bevor man es mit dem Auge sieht.",
    scale: "0.3+ feucht · 0.1–0.3 normal · <0.1 trocken",
  },
  ET0: {
    short: "Wasserverdunstungs-Referenz (mm/Tag)",
    long: "ET₀ sagt dir, wie viel Wasser theoretisch heute verdunstet – aus Boden und Pflanzen zusammen. Hohe ET₀ + trockener Boden = dringender Bewässerungsbedarf.",
    scale: "<2 mm ruhig · 2–4 mm normal · >5 mm hoch",
  },
  GDD: {
    short: "Wärmesumme fürs Wachstum (°C·Tage)",
    long: "Growing Degree Days summieren die Wärme, die Pflanzen brauchen, um ihre Entwicklungsstufen zu erreichen. Je schneller GDD steigt, desto früher reift dein Bestand.",
    scale: "Basis 10 °C – kumulativ über die Saison",
  },
  SoilMoisture: {
    short: "Bodenfeuchte in m³/m³",
    long: "Wie viel Wasser steckt in deinem Boden? Gemessen als Volumenanteil. Obere Schicht (0–10 cm) reagiert schnell auf Regen; tiefere (10–40 cm) zählt für Wurzeln & Ertrag.",
    scale: "0.35+ feucht · 0.25 normal · 0.15 trocken · <0.15 kritisch",
  },
  YieldIndex: {
    short: "Ernte-Ausblick 0–100",
    long: "Unser eigener Index kombiniert NDVI, Bodenfeuchte und Wetter zu einer einfachen Prognose. 70+ heißt: alles spricht für eine solide Ernte. Unter 40: frühzeitig Gegensteuern.",
    scale: "70+ gut · 50–70 normal · 30–50 schwach · <30 Risiko",
  },
  ForestVitality: {
    short: "Wald-Vitalitäts-Index 0–100",
    long: "Kombiniert NDMI, Seenqualität und NDVI-Trend. Zeigt auf einen Blick, ob dein Forst & die umliegenden Gewässer gesund sind – bevor Borkenkäfer oder Trockenheit sichtbar werden.",
    scale: "80+ vital · 60–80 OK · 40–60 angespannt · <40 gefährdet",
  },
};
